/**
 * Organizations state (screenshots/56–66): hydrated before the first render from the snapshots the Worker
 * inlines (snapshot:organizations, snapshot:organization:<id>), then changed optimistically through
 * /api/dashboard/orgs/* (worker/src/dashboard-orgs.js). Selectors return stored references only.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import type { Position } from "../forecast/forecast";
import { toastStore } from "../stores/toastStore";
import { shortName } from "./build";
import type { CoalitionEntry, MeetingLogEntry, OrgAlerts, OrgListData, OrgProfileSnapshot } from "./types";

interface OrgState {
  list: OrgListData | null;
  profile: OrgProfileSnapshot | null;
  /** Bumped when a follow flips, so the button can spring (screenshots/66 #5). */
  followPulse: number;
  hydrateList: (l: OrgListData) => void;
  hydrateProfile: (p: OrgProfileSnapshot) => void;
  setFollowing: (on: boolean, opts?: { undo?: boolean }) => Promise<void>;
  setAlert: (k: keyof OrgAlerts, on: boolean) => Promise<void>;
  setOverride: (recordId: string, position: Position | null) => Promise<void>;
  addMeeting: (e: Pick<MeetingLogEntry, "date" | "who" | "note">) => Promise<boolean>;
  saveCoalition: (e: { clientId: string; name: string; side: "ally" | "opponent"; note: string }) => Promise<boolean>;
}

const api = (path: string, method: string, body?: unknown) => fetch(`/api/dashboard/orgs${path}`, {
  method, credentials: "include", ...(body !== undefined ? { headers: { "content-type": "application/json" }, body: JSON.stringify(body) } : {}),
}).then((r) => (r.ok ? r.json() : null)).catch(() => null);

const ALERT_COUNT = (a: OrgAlerts) => [a.newFiling, a.newContribution, a.lobbiesOnFollowed].filter(Boolean).length;

export const orgStore = createStore<OrgState>()((set, get) => ({
  list: null,
  profile: null,
  followPulse: 0,
  hydrateList: (list) => set({ list }),
  hydrateProfile: (profile) => set({ profile }),
  setFollowing: async (on, { undo = false } = {}) => {
    const p = get().profile;
    if (!p) return;
    set({ profile: { ...p, following: on }, followPulse: get().followPulse + 1 });
    const r = await api(`/${encodeURIComponent(p.org.id)}/follow`, "PUT", { following: on });
    if (!r) { set({ profile: { ...get().profile!, following: !on } }); toastStore.getState().show({ message: "Couldn't change that. Try again." }); return; }
    set({ profile: { ...get().profile!, following: r.following, alerts: r.alerts ?? get().profile!.alerts } });
    if (on && !undo) {
      // One click follows and turns on sensible alerts; a toast confirms with Undo (screenshots/66 #5).
      toastStore.getState().show({ message: `Following ${shortName(p.org.name)}. ${ALERT_COUNT(r.alerts ?? p.alerts)} alerts on.`, actionLabel: "Undo", onAction: () => void get().setFollowing(false, { undo: true }) });
    }
  },
  setAlert: async (k, on) => {
    const l = get().list;
    if (!l) return;
    set({ list: { ...l, alerts: { ...l.alerts, [k]: on } } });
    const r = await api("/alerts", "PUT", { [k]: on });
    if (!r) { set({ list: { ...get().list!, alerts: { ...get().list!.alerts, [k]: !on } } }); toastStore.getState().show({ message: "Couldn't change that. Try again." }); }
  },
  setOverride: async (recordId, position) => {
    const p = get().profile;
    if (!p) return;
    const prev = p.org.items.find((i) => i.recordId === recordId)?.override ?? null;
    const apply = (o: typeof prev) => {
      const cur = get().profile!;
      set({ profile: { ...cur, org: { ...cur.org, items: cur.org.items.map((i) => (i.recordId === recordId ? { ...i, override: o } : i)) } } });
    };
    apply(position ? { position, by: "Your team" } : null);
    const r = await api(`/${encodeURIComponent(p.org.id)}/override`, "PUT", { recordId, position });
    if (!r) { apply(prev); toastStore.getState().show({ message: "Couldn't save that. Try again." }); return; }
    toastStore.getState().show({ message: position ? "Override saved for your team's forecasts." : "Override removed.", actionLabel: "Undo",
      onAction: () => { apply(prev); void api(`/${encodeURIComponent(p.org.id)}/override`, "PUT", { recordId, position: prev?.position ?? null }); } });
  },
  addMeeting: async (e) => {
    const p = get().profile;
    if (!p) return false;
    const r = (await api(`/${encodeURIComponent(p.org.id)}/meetings`, "POST", e)) as MeetingLogEntry | null;
    if (!r) { toastStore.getState().show({ message: "Couldn't save that. Try again." }); return false; }
    set({ profile: { ...get().profile!, meetingLog: [r, ...(get().profile!.meetingLog ?? [])] } });
    return true;
  },
  saveCoalition: async (e) => {
    const l = get().list;
    const r = (await api("/coalitions", "POST", e)) as { entry: CoalitionEntry | null } | null;
    if (!r) { toastStore.getState().show({ message: "Couldn't save that. Try again." }); return false; }
    if (l) {
      const rest = l.coalitions.filter((c) => c.clientId !== e.clientId);
      set({ list: { ...get().list!, coalitions: r.entry ? [...rest, r.entry] : rest } });
    }
    return true;
  },
}));

export const useOrgs = <T,>(selector: (s: OrgState) => T) => useStore(orgStore, selector);
